import { useMemo } from "react";
import { pad, wdLabel, wdColor } from "@/lib/yasamaru";

export function MonthCalendar({ period, days, shifts, requests, onDateClick, selectedDate, compact = false }) {
  const closedDates = useMemo(() => new Set(period?.closed_dates || []), [period]);

  const shiftByDate = useMemo(() => {
    const m = {};
    for (const s of shifts) {
      if (s.status === "cancelled" || s.status === "draft") continue;
      m[s.date] = m[s.date] || [];
      m[s.date].push(s);
    }
    return m;
  }, [shifts]);

  const reqByDate = useMemo(() => {
    const m = {};
    for (const r of requests) {
      m[r.date] = m[r.date] || { available: 0, either: 0, unavailable: 0 };
      m[r.date][r.request_type] = (m[r.date][r.request_type] || 0) + 1;
    }
    return m;
  }, [requests]);

  if (!period) return null;
  const requiredByWd = period.weekday_required_staff || { "0": 4, "1": 2, "2": 2, "3": 2, "4": 2, "5": 3, "6": 4 };

  // Build full 6x7 grid: prev month tail + current month + next month head
  const firstWd = new Date(period.year, period.month - 1, 1).getDay();
  const prevMonthDays = new Date(period.year, period.month - 1, 0).getDate();
  const cells = [];
  // prev month tail (faded)
  for (let i = firstWd - 1; i >= 0; i--) {
    cells.push({ blank: false, outside: true, k: `p${i}`, day: prevMonthDays - i });
  }
  for (let d = 1; d <= days; d++) {
    const dateStr = `${period.year}-${pad(period.month)}-${pad(d)}`;
    const wd = new Date(period.year, period.month - 1, d).getDay();
    const placed = shiftByDate[dateStr] || [];
    const req = reqByDate[dateStr] || {};
    const candidates = (req.available || 0) + (req.either || 0); // ◎+△ = 候補
    const closed = closedDates.has(dateStr);
    const required = Number(requiredByWd[String(wd)] ?? requiredByWd[wd] ?? 2);
    let tone, status, statusTone;
    if (closed) { tone = "bg-slate-100 border-slate-200"; status = "休業日"; statusTone = "bg-slate-300 text-slate-600"; }
    else if (placed.length === 0) { tone = "bg-slate-50 border-slate-200"; status = "空き"; statusTone = "bg-slate-200 text-slate-700"; }
    else if (placed.length < required) { tone = "bg-amber-50 border-amber-200"; status = "少なめ"; statusTone = "bg-amber-200 text-amber-900"; }
    else if (placed.length === required) { tone = "bg-emerald-50 border-emerald-200"; status = "OK"; statusTone = "bg-emerald-200 text-emerald-900"; }
    else { tone = "bg-rose-50 border-rose-200"; status = "多め"; statusTone = "bg-rose-200 text-rose-900"; }
    cells.push({ blank: false, outside: false, k: dateStr, day: d, weekday: wd, dateStr, placed: placed.length, candidates, required, status, statusTone, tone, closed });
  }
  // next month head to fill to 42 cells (6 weeks)
  let nd = 1;
  while (cells.length < 42) {
    cells.push({ blank: false, outside: true, k: `n${nd}`, day: nd });
    nd++;
  }

  return (
    <div data-testid="month-calendar">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="text-base font-bold text-slate-900">{period.year}年{period.month}月 <span className="text-slate-500 font-normal text-sm">日別カレンダー</span></h3>
        <div className="flex flex-wrap gap-3 text-[11px] text-slate-600">
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 bg-rose-300 rounded-full"></span>多め</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 bg-emerald-400 rounded-full"></span>OK</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 bg-amber-300 rounded-full"></span>少なめ</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 bg-slate-300 rounded-full"></span>空き</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 bg-slate-400 rounded-full"></span>休業日</span>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {["日", "月", "火", "水", "木", "金", "土"].map((w, i) => (
          <div key={w} className={`text-center text-xs font-bold py-1 ${i === 0 ? "text-red-500" : i === 6 ? "text-blue-500" : "text-slate-600"}`}>{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c) => c.outside ? (
          <div key={c.k} className={`rounded border border-slate-100 bg-slate-50/50 p-2 ${compact ? "min-h-[44px]" : "min-h-[78px]"}`}>
            <span className="text-xs text-slate-300">{c.day}</span>
          </div>
        ) : compact ? (
          // ===== Compact mode (mobile / tablet): 日付 + 状態ドット =====
          <button
            key={c.k} type="button"
            data-testid={`cal-day-${c.day}`}
            onClick={() => !c.closed && onDateClick(c.dateStr)}
            disabled={c.closed}
            className={`relative flex flex-col items-center justify-center rounded border-2 min-h-[52px] py-1 transition-all hover:shadow active:scale-95 ${c.tone} ${
              selectedDate === c.dateStr ? "ring-2 ring-blue-500 border-blue-500" : ""
            } ${c.closed ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
          >
            <span className={`font-bold text-base leading-none ${wdColor(c.weekday)}`}>{c.day}</span>
            {c.closed ? (
              <span className="text-[9px] text-slate-500 mt-0.5">休</span>
            ) : (
              <span className="mt-0.5 text-[10px] font-semibold leading-none text-slate-700">
                <b className="text-slate-900 text-[11px]">{c.placed}</b>
                <span className="text-slate-400">/{c.required}</span>
              </span>
            )}
            {/* 状態ドット */}
            <span className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
              c.closed ? "bg-slate-400" :
              c.placed === 0 ? "bg-slate-300" :
              c.placed < c.required ? "bg-amber-400" :
              c.placed === c.required ? "bg-emerald-500" :
              "bg-rose-400"
            }`} />
          </button>
        ) : (
          <button
            key={c.k} type="button"
            data-testid={`cal-day-${c.day}`}
            onClick={() => !c.closed && onDateClick(c.dateStr)}
            disabled={c.closed}
            className={`text-left rounded border-2 p-2 min-h-[78px] transition-all hover:shadow-md hover:border-blue-400 ${c.tone} ${
              selectedDate === c.dateStr ? "ring-2 ring-blue-500 border-blue-500" : ""
            } ${c.closed ? "cursor-not-allowed" : "cursor-pointer"}`}
          >
            <div className="flex items-baseline justify-between">
              <span className={`font-bold text-sm ${wdColor(c.weekday)}`}>{c.day}</span>
              <span className="text-[9px] text-slate-500">({wdLabel(c.weekday)})</span>
            </div>
            {!c.closed && (
              <div className="mt-1.5 space-y-0.5 text-[11px] leading-tight">
                <div className="flex items-baseline gap-1">
                  <span className="text-slate-500 text-[9px]">出勤</span>
                  <b className="text-slate-900 text-sm">{c.placed}</b>
                  <span className="text-[9px] text-slate-500">人</span>
                </div>
                {c.candidates > 0 && (
                  <div className="flex items-baseline gap-1 text-slate-500">
                    <span className="text-[9px]">+候補</span>
                    <b className="text-[11px]">{c.candidates}</b>
                  </div>
                )}
              </div>
            )}
            <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded font-semibold ${c.statusTone}`}>{c.status}</span>
          </button>
        ))}
      </div>
      {compact ? (
        <p className="text-[10px] text-slate-400 mt-2">※ 各セル「出勤人数 / 必要人数」。日付をタップで詳細を表示します。</p>
      ) : (
        <p className="text-[10px] text-slate-400 mt-2">※ 出勤=配置済み人数 / 候補=◎入れます + △必要なら の合計</p>
      )}
    </div>
  );
}
